import { WidgetQueryParams } from "@app-contexts/sdk/services/Widget";
import { CUSTOMER_ATTRIBUTION_QUERY_PARAM } from "@shared/modules/basket/session/customer-attribution-messaging";

const URL_PARSE_BASE = "https://showpass.invalid";
const MAX_URL_ENCODING_DEPTH = 3;

export function omitCustomerAttributionToken(
  params: WidgetQueryParams
): WidgetQueryParams {
  const {
    [CUSTOMER_ATTRIBUTION_QUERY_PARAM]: _customerAttributionToken,
    ...safeParams
  } = params;
  return safeParams;
}

function rebuildRelativeUrl(original: string, url: URL): string {
  const suffix = `${url.pathname}${url.search}${url.hash}`;
  if (original.startsWith("//")) return `//${url.host}${suffix}`;
  if (original.startsWith("/")) return suffix;
  if (original.startsWith("?")) return `${url.search}${url.hash}`;
  if (original.startsWith("#")) return url.hash;

  const queryIndex = original.indexOf("?");
  const hashIndex = original.indexOf("#");
  const pathEnd = [queryIndex, hashIndex]
    .filter((index) => index >= 0)
    .reduce((smallest, index) => Math.min(smallest, index), original.length);
  return `${original.slice(0, pathEnd)}${url.search}${url.hash}`;
}

function stripTokenFromDecodedUrl(value: string): string | undefined {
  let url: URL;
  try {
    url = new URL(value, URL_PARSE_BASE);
  } catch {
    return undefined;
  }

  if (!url.searchParams.has(CUSTOMER_ATTRIBUTION_QUERY_PARAM)) {
    return undefined;
  }

  url.searchParams.delete(CUSTOMER_ATTRIBUTION_QUERY_PARAM);
  const isAbsolute = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(value);
  return isAbsolute ? url.toString() : rebuildRelativeUrl(value, url);
}

export function stripCustomerAttributionTokenFromUrl(value: string): string {
  let decodedValue = value;

  for (
    let encodingDepth = 0;
    encodingDepth <= MAX_URL_ENCODING_DEPTH;
    encodingDepth += 1
  ) {
    const strippedUrl = stripTokenFromDecodedUrl(decodedValue);
    if (strippedUrl !== undefined) {
      let encodedUrl = strippedUrl;
      for (let index = 0; index < encodingDepth; index += 1) {
        encodedUrl = encodeURIComponent(encodedUrl);
      }
      return encodedUrl;
    }

    try {
      const nextDecodedValue = decodeURIComponent(decodedValue);
      if (nextDecodedValue === decodedValue) break;
      decodedValue = nextDecodedValue;
    } catch {
      break;
    }
  }

  return value;
}

/**
 * Carries the host-supplied attribution token across a widget transition.
 * Parameters emitted by a child iframe are never trusted to set or replace it.
 */
export function inheritCustomerAttributionToken(
  trustedParams: WidgetQueryParams,
  transitionParams: WidgetQueryParams
): WidgetQueryParams {
  const {
    customer_attribution_token: _untrustedAttributionToken,
    ...safeTransitionParams
  } = transitionParams;
  const trustedAttributionToken = trustedParams.customer_attribution_token;

  if (
    typeof trustedAttributionToken !== "string" ||
    trustedAttributionToken.trim().length === 0
  ) {
    return safeTransitionParams;
  }

  return {
    ...safeTransitionParams,
    customer_attribution_token: trustedAttributionToken,
  };
}
